import {useState, useEffect, useRef} from 'react'
import {Box, Button, Flex, HStack, Link, Text, VStack, Switch, Field} from "@chakra-ui/react";
import {useColorModeValue} from "./ui/color-mode";
import {useTranslation} from 'react-i18next'
import {Link as RouterLink} from 'react-router-dom'
import {analytics} from '../utils/analytics'
import {useAppSelector} from '../hooks/store'
import {isBarberLoggedIn} from '../slices/authSlice'
import {useUpdateAnalyticsConsentMutation} from '../services/userService'

const CONSENT_KEY = 'barbermate_cookie_consent'

interface CookiePreferences {
    analytics: boolean
    errorMonitoring: boolean
    timestamp: string
}

function getStoredConsent(): CookiePreferences | null {
    try {
        const stored = localStorage.getItem(CONSENT_KEY)
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

function saveConsent(prefs: CookiePreferences) {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs))
    if (prefs.analytics) {
        analytics.enableTracking()
    } else {
        analytics.disableTracking()
    }
}

export default function CookieConsent() {
    const {t} = useTranslation()
    const [visible, setVisible] = useState(false)
    const [showPreferences, setShowPreferences] = useState(false)
    const [analytics, setAnalytics] = useState(true)
    const [errorMonitoring, setErrorMonitoring] = useState(true)

    const bgColor = useColorModeValue('white', 'gray.800')
    const borderColor = useColorModeValue('gray.200', 'gray.600')
    const textColor = useColorModeValue('gray.600', 'gray.300')

    const barberLogged = useAppSelector(isBarberLoggedIn)
    const [updateAnalyticsConsent] = useUpdateAnalyticsConsentMutation()

    useEffect(() => {
        const consent = getStoredConsent()
        if (!consent) {
            setVisible(true)
        }
    }, [])

    // The banner is fixed-bottom with z-index 9999. On mobile it covered the
    // booking flow's BACK/NEXT row, making them untappable until consent
    // (the app scrolls inside App.tsx's overflowX-hidden Box, not the body).
    // While open: publish the banner height as --bm-consent-h (the app shell
    // pads its scroll container by it) and hide the BugHop feedback launcher
    // (floats in the same corner as our buttons) via a body class — index.css.
    const bannerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!visible) return
        document.body.classList.add('bm-consent-open')
        const el = bannerRef.current
        const apply = () => {
            document.documentElement.style.setProperty('--bm-consent-h', `${el?.offsetHeight ?? 0}px`)
        }
        apply()
        const ro = el ? new ResizeObserver(apply) : null
        if (el && ro) ro.observe(el)
        return () => {
            ro?.disconnect()
            document.body.classList.remove('bm-consent-open')
            document.documentElement.style.removeProperty('--bm-consent-h')
        }
    }, [visible])

    if (!visible) return null

    const persistConsent = (analyticsConsent: boolean) => {
        if (!barberLogged) return
        updateAnalyticsConsent({analytics_consent: analyticsConsent}).unwrap().catch(() => {
            // Non-fatal — BE consent state will be picked up next checkout via metadata.
        })
    }

    const handleAcceptAll = () => {
        saveConsent({analytics: true, errorMonitoring: true, timestamp: new Date().toISOString()})
        persistConsent(true)
        setVisible(false)
    }

    const handleRejectNonEssential = () => {
        saveConsent({analytics: false, errorMonitoring: false, timestamp: new Date().toISOString()})
        persistConsent(false)
        setVisible(false)
    }

    const handleSavePreferences = () => {
        saveConsent({analytics, errorMonitoring, timestamp: new Date().toISOString()})
        persistConsent(analytics)
        setVisible(false)
    }

    return (
        <Box
            ref={bannerRef}
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            zIndex={9999}
            bg={bgColor}
            borderTop="1px solid"
            borderColor={borderColor}
            boxShadow="0 -4px 20px rgba(0, 0, 0, 0.1)"
            p={{base: 4, md: 6}}
        >
            <Box maxW="1200px" mx="auto">
                {!showPreferences ? (
                    <Flex
                        direction={{base: 'column', md: 'row'}}
                        align={{base: 'stretch', md: 'center'}}
                        gap={4}
                    >
                        <VStack align="start" gap={1} flex={1}>
                            <Text fontWeight="600" fontSize="sm">{t('cookieConsent.title')}</Text>
                            <Text fontSize="sm" color={textColor}>
                                {t('cookieConsent.description')}{' '}
                                <Link asChild color="#0088FF"><RouterLink to="/cookies">{t('cookieConsent.learnMore')}</RouterLink></Link>
                            </Text>
                        </VStack>
                        <HStack gap={3} flexShrink={0} flexWrap="wrap">
                            <Button size="sm" variant="outline" onClick={() => setShowPreferences(true)}>
                                {t('cookieConsent.manage')}
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleRejectNonEssential}>
                                {t('cookieConsent.rejectNonEssential')}
                            </Button>
                            <Button size="sm" bg="#0088FF" color="white" _hover={{bg: '#006DD6'}} onClick={handleAcceptAll}>
                                {t('cookieConsent.acceptAll')}
                            </Button>
                        </HStack>
                    </Flex>
                ) : (
                    <VStack align="stretch" gap={4}>
                        <Text fontWeight="600" fontSize="sm">{t('cookieConsent.preferencesTitle')}</Text>

                        <Field.Root display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                            <Field.Label htmlFor="essential" mb="0" fontSize="sm">
                                {t('cookieConsent.essential')}
                            </Field.Label>
                            <Switch.Root id="essential" checked disabled colorPalette="green">
                                <Switch.HiddenInput/>
                                <Switch.Control><Switch.Thumb/></Switch.Control>
                            </Switch.Root>
                        </Field.Root>

                        <Field.Root display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                            <VStack align="start" gap={0}>
                                <Field.Label htmlFor="analytics" mb="0" fontSize="sm">
                                    {t('cookieConsent.analytics')}
                                </Field.Label>
                                <Text fontSize="xs" color={textColor}>{t('cookieConsent.analyticsDesc')}</Text>
                            </VStack>
                            <Switch.Root id="analytics" checked={analytics} onCheckedChange={(e) => setAnalytics(e.checked)} colorPalette="blue">
                                <Switch.HiddenInput/>
                                <Switch.Control><Switch.Thumb/></Switch.Control>
                            </Switch.Root>
                        </Field.Root>

                        <Field.Root display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                            <VStack align="start" gap={0}>
                                <Field.Label htmlFor="errorMonitoring" mb="0" fontSize="sm">
                                    {t('cookieConsent.errorMonitoring')}
                                </Field.Label>
                                <Text fontSize="xs" color={textColor}>{t('cookieConsent.errorMonitoringDesc')}</Text>
                            </VStack>
                            <Switch.Root id="errorMonitoring" checked={errorMonitoring} onCheckedChange={(e) => setErrorMonitoring(e.checked)} colorPalette="blue">
                                <Switch.HiddenInput/>
                                <Switch.Control><Switch.Thumb/></Switch.Control>
                            </Switch.Root>
                        </Field.Root>

                        <HStack gap={3} justify="flex-end">
                            <Button size="sm" variant="outline" onClick={() => setShowPreferences(false)}>
                                {t('cookieConsent.back')}
                            </Button>
                            <Button size="sm" bg="#0088FF" color="white" _hover={{bg: '#006DD6'}} onClick={handleSavePreferences}>
                                {t('cookieConsent.savePreferences')}
                            </Button>
                        </HStack>
                    </VStack>
                )}
            </Box>
        </Box>
    )
}
